import { APIService } from '../../../lib/api/APIService';

const path = (value) => encodeURIComponent(value);

/**
 * /api/organisations/students/<id>/activity/<segments...>?<query>, every
 * path segment encoded, and query values left out when null or undefined.
 */
const activityUrl = (studentId, segments, query = {}) => {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value != null && value !== '') params.set(key, String(value));
  });
  const search = params.toString();
  const tail = segments.map(path).join('/');
  return `/api/organisations/students/${path(studentId)}/activity/${tail}${search ? `?${search}` : ''}`;
};

// Organisations API: /api/organisations. Joining, the caller's own
// membership, and the staff dashboard's scopes, students and groups.
// Errors carry { error: { code, message } } in APIError.data; see
// ../lib/errorMessage for showing them. No response ever carries the staff
// code; getStudentLink carries the student code, which is meant to be
// shared with students.
class OrganisationsAPI extends APIService {
  async join(code) {
    // No automatic retry: a join that succeeded but timed out would be
    // retried into a misleading 409 already_member.
    return this.post('/api/organisations/join', { code }, { skipRetry: true });
  }

  /** { is_platform_admin, membership: { organisation, role, cohort_year, status, groups } | null } */
  async me() {
    return this.get('/api/organisations/me');
  }

  /** { organisations: [...], default_scope } — staff and platform admins only (403 otherwise). */
  async getScopes() {
    return this.get('/api/organisations/scopes');
  }

  /**
   * { scope, period, period_label, students: [...] }: per student its
   * completions, last_active_at, active_days and forms. No `scope` means the
   * default scope.
   */
  async getStudents({ scope, period }) {
    const params = new URLSearchParams({ period });
    if (scope) params.set('scope', scope);
    return this.get(`/api/organisations/students?${params.toString()}`);
  }

  /**
   * { scope, students: [{ id, name, email, forms, documents: { cv, cover_letter,
   * personal_statement } }] }: each the current version ({ tracker_document_id,
   * version, earlier, created_at, filename, file_size, mime_type }) or null.
   * Not filtered by any period, and never carries a URL.
   */
  async getDocuments({ scope }) {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    const search = params.toString();
    return this.get(`/api/organisations/documents${search ? `?${search}` : ''}`);
  }

  /**
   * { url, expires_at }: a link to one of the student's documents that
   * expires in five minutes. 403 when the viewer may not see the student,
   * 404 when the document is not theirs, 503 when no link can be made. No
   * retry: each call makes a new link.
   */
  async getDocumentLink(studentId, trackerDocumentId) {
    return this.get(
      `/api/organisations/students/${path(studentId)}/documents/${path(trackerDocumentId)}/link`,
      { skipRetry: true },
    );
  }

  /**
   * { url, expires_at } for one application-builder draft. A new link on
   * every call. 403 when the viewer cannot see the student, 404 otherwise
   * when the file is not one of theirs, 503 when no link can be made.
   */
  async getBuilderDocumentLink(studentId, userDocumentId) {
    return this.get(
      `/api/organisations/students/${path(studentId)}/builder-documents/${path(userDocumentId)}/link`,
      { skipRetry: true },
    );
  }

  /** { student: { id, name, email, cohort_year, year_group, groups }, sections } — the profile header and tabs. */
  async getStudentProfile(studentId) {
    return this.get(`/api/organisations/students/${path(studentId)}`);
  }

  /** { period, period_label, generated_at, stats, completions, flags } */
  async getStudentActivitySummary(studentId, { period }) {
    return this.get(activityUrl(studentId, ['summary'], { period }));
  }

  /** { period, period_label, generated_at, items: [WritingItem], next_cursor } */
  async getStudentActivityWriting(studentId, { period, cursor, limit } = {}) {
    return this.get(activityUrl(studentId, ['writing'], { period, cursor, limit }));
  }

  /** One essay (tara_essay | lnat_essay): { ...WritingItem, scores, feedback, work, comments }; 404 when not this student's. */
  async getStudentWritingPiece(studentId, kind, id) {
    return this.get(activityUrl(studentId, ['writing', kind, id]));
  }

  /** { essays: [{ id, completed_at }], traits: [{ key, label, levels }] }, oldest essay first. */
  async getStudentTraitTrends(studentId, { limit } = {}) {
    return this.get(activityUrl(studentId, ['writing', 'trait-trends'], { limit }));
  }

  /** { period, period_label, generated_at, exams: [{ exam, label, papers, weekly_accuracy, topics, habits }] } */
  async getStudentActivityExams(studentId, { period }) {
    return this.get(activityUrl(studentId, ['exams'], { period }));
  }

  /** { period, period_label, generated_at, runs: [InterviewRun], next_cursor } */
  async getStudentActivityInterviews(studentId, { period, cursor, limit } = {}) {
    return this.get(activityUrl(studentId, ['interviews'], { period, cursor, limit }));
  }

  /** { turns: [{ number, speaker, text }] }; 404 when the run is not this student's. */
  async getStudentInterviewTranscript(studentId, runId) {
    return this.get(activityUrl(studentId, ['interviews', runId, 'transcript']));
  }

  /** { period, period_label, generated_at, summary, cases: [CaseEntry], next_cursor } */
  async getStudentActivityCases(studentId, { period, cursor, limit } = {}) {
    return this.get(activityUrl(studentId, ['cases'], { period, cursor, limit }));
  }

  /** { reasoning_moves: [{ label, strength, uses }], concepts: [...] }, across the student's whole history. */
  async getStudentCaseConcepts(studentId) {
    return this.get(activityUrl(studentId, ['cases', 'concepts']));
  }

  /** { kind: 'tutorial'|'discussion', items: [{ number, text, band }] }; 404 when not this student's. */
  async getStudentCaseAttempt(studentId, attemptId) {
    return this.get(activityUrl(studentId, ['cases', attemptId]));
  }

  /**
   * One written case answer in full: { attempt_id, case_title, completed_at, answer_text, word_count,
   * has_feedback, mark, scores, verdict, missed_from_case, how_to_fix, comments }; 404 when not this student's.
   */
  async getStudentCaseWrittenAnswer(studentId, attemptId) {
    return this.get(activityUrl(studentId, ['cases', 'written', attemptId]));
  }

  /**
   * One paper part, question by question: { attempt, summary, questions }
   * (student_insights/types.py PaperReviewResponse). `attemptId` is a
   * quiz_attempts id from an Exam practice row's parts; 404 when not this
   * student's.
   */
  async getStudentPaperReview(studentId, attemptId) {
    return this.get(activityUrl(studentId, ['papers', attemptId]));
  }

  /** The same shape for a pathbuddy.app set-paper session (a part whose source is "app_session"). */
  async getStudentAppPaperReview(studentId, sessionId) {
    return this.get(activityUrl(studentId, ['papers', 'app', sessionId]));
  }

  /**
   * The student's custom quizzes in the period, newest first: { summary,
   * quizzes, next_cursor, questions_saved_since } (student_insights/types.py
   * QuizzesResponse). `cursor` is the previous page's next_cursor.
   */
  async getStudentQuizzes(studentId, { period, cursor, limit } = {}) {
    return this.get(activityUrl(studentId, ['quizzes'], { period, cursor, limit }));
  }

  /**
   * One custom quiz, question by question: { quiz, summary, questions }
   * (QuizReviewResponse). `sessionId` is a quiz row's session_id; 404 when
   * it is not this student's.
   */
  async getStudentQuizReview(studentId, sessionId) {
    return this.get(activityUrl(studentId, ['quizzes', sessionId]));
  }

  /** { choices: [CourseChoice], documents: [DocumentSummary] }; `period` decides "practised". */
  async getStudentActivityApplications(studentId, { period } = {}) {
    return this.get(activityUrl(studentId, ['applications'], { period }));
  }

  /**
   * One application element's version history, all time (no period):
   * { element, label, moves: [{ key, label }], points, never_shown: [key] }.
   * `element` is ps_q1, ps_q2, ps_q3, cv or cl (400 otherwise). Points are
   * oldest first, numbered 1..n across both kinds: { kind: 'feedback', at,
   * number, moves_shown, total_moves, states: { [move key]: state } } or
   * { kind: 'upload', at, number, tracker_document_id, version_number }.
   */
  async getStudentApplicationHistory(studentId, element) {
    return this.get(activityUrl(studentId, ['applications', element, 'history']));
  }

  /** { period, period_label, generated_at, by_app: [{ key, label, items }], calendar: { days } } */
  async getStudentActivityEngagement(studentId, { period }) {
    return this.get(activityUrl(studentId, ['engagement'], { period }));
  }

  /** Chat counts for one student. No message text. */
  async getStudentActivityChat(studentId, { period }) {
    return this.get(activityUrl(studentId, ['chat'], { period }));
  }

  /**
   * Every conversation, all time, newest last-message first:
   * { conversations, next_cursor, total }. `mode` is Planning, Coach,
   * Builder, General or Onboarding; omit it for all of them.
   */
  async getStudentChatConversations(studentId, { mode, cursor, limit } = {}) {
    return this.get(activityUrl(studentId, ['chat', 'conversations'], { mode, cursor, limit }));
  }

  /** One conversation, oldest message first: { id, title, mode, messages }. */
  async getStudentChatTranscript(studentId, conversationId) {
    return this.get(activityUrl(studentId, ['chat', 'conversations', conversationId]));
  }

  /**
   * { data_as_of, assessment_points, subjects, predicted_grades, ucas_points,
   * attendance_percentage, lates }. 403 when the viewer may not see the
   * student, 404 when the school does not have the feature.
   */
  async getStudentSchoolRecord(studentId) {
    return this.get(`/api/organisations/students/${path(studentId)}/school-record`);
  }

  /**
   * The school's background details for a student, for its org admin only.
   * Not read-only in effect: the server logs every read as a view, so call
   * it only when the viewer opens the details, never to prefetch. No
   * automatic retry: a read that succeeded but timed out would log twice.
   */
  async getStudentSensitiveDetails(studentId) {
    return this.get(`/api/organisations/students/${path(studentId)}/sensitive-details`, { skipRetry: true });
  }

  /** { path: "/join#<student code>" } */
  async getStudentLink(organisationId) {
    return this.get(`/api/organisations/${path(organisationId)}/student-link`);
  }

  /** { group: { id, organisation_id, group_type, name, scope } } */
  async createGroup(organisationId, name) {
    // No retry: a create that succeeded but timed out would make a second group.
    return this.post(`/api/organisations/${path(organisationId)}/groups`, { name }, { skipRetry: true });
  }

  async deleteGroup(groupId) {
    return this.delete(`/api/organisations/groups/${path(groupId)}`);
  }

  async addGroupMember(groupId, userId) {
    return this.put(`/api/organisations/groups/${path(groupId)}/members/${path(userId)}`);
  }

  async removeGroupMember(groupId, userId) {
    return this.delete(`/api/organisations/groups/${path(groupId)}/members/${path(userId)}`);
  }
}

export const organisationsAPI = new OrganisationsAPI();
